/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  FontFamily,
  FontSize,
  JSDateFormat,
  Options,
  PrepAndSendRequest,
  RequestData,
  Settings,
  Signature,
  SignatureType,
  User,
  AccountDateFormat,
  File,
  Type,
  Role,
  Attachment as UnifiedJsonAttachment,
  DeliveryType,
  Template as UnifiedJsonTemplate,
  PHPDateFormat,
  CoverPage,
  BulkSend,
  EditorData,
  Rule,
  StringArrayTrigger,
  StringTrigger,
  NonNumericTrigger,
  NumericTrigger,
  StringOperator,
  NonNumericOperator,
  NumericOperator,
  StringArrayOperator,
  Recipient,
  Signer,
  Page,
  Document,
} from 'generated/types/HelloRequest';
import { Field, FieldTypes } from 'hellospa/components/editor/types';
import { Attachment, CCEmail, CCRole, CCTypes, FilePollResponseStatus, RecipientTypes, RequestTypes, UserFileTypes } from 'hellospa/page/prep-and-send/data/types';
import { DataSchema, SaveDataType } from './prep-and-send';
import { UnreachableError } from 'hellospa/ts-utils';
import { convertIntegrations, convertFields, getAutoFilledType } from './editor-converters';
import uuid from 'uuid/v4';
import { identity } from 'lodash';
import { Trigger } from 'hellospa/conditional-logic';


export function convertForUnifiedJSON(
  prepAndSendData: SaveDataType,
  user: DataSchema['user'],
  uploadIntegrations: DataSchema['flags']['uploadIntegrations'],
): PrepAndSendRequest {
  const { guid, settings, editorFields, requestType } = prepAndSendData;
  return {
    guid,
    settings: convertSettingsForUnifiedJSON(settings),
    options: convertOptionsForUnifiedJSON(settings, editorFields.fields),
    flags: {
      requestType,
    },
    accountSettings: {
      allowTemplateDelete: false,
      uploadIntegrations: {
        ...uploadIntegrations,
      },
    },
    requestData: convertToUnifiedJSONRequestData(prepAndSendData),
    user: convertUserForUnifiedJSON(user),
  };
}

function convertUserForUnifiedJSON(user: DataSchema['user']): User {
  return {
    ...user,
    apiIdsEnabled: true,
    isConfirmedAccount: true,
    accountDateFormat: (user.accountDateFormat in AccountDateFormat
      ? user.accountDateFormat
      : AccountDateFormat.MMDDYYYY) as AccountDateFormat,
  };
}

function convertSettingsForUnifiedJSON(settings: DataSchema['data']['settings']): Settings {
  return {
    ...settings,
  };
}

function convertOptionsForUnifiedJSON(settings: SaveDataType['settings'], fields: Field[]): Options {
  return {
    hideContinueButton: false,
    requireSignerInfo: settings.requireSignerInfo,
    dateFormat: getDateFormatForOptions(fields),
  };
}

function getDateFormatForOptions(fields: Field[]): JSDateFormat {
  const dateFormat = JSDateFormat.JSDefault;
  const reducer = (defaultDateFormat: JSDateFormat, field: Field): JSDateFormat => {
    if (field.type === FieldTypes.Date && field.dateFormat) {
      return field.dateFormat as JSDateFormat;
    }
    return defaultDateFormat;
  };
  return fields.reduce(reducer, dateFormat);
}

function convertToUnifiedJSONRequestData(data: SaveDataType): RequestData {
  const unifiedData = {
    files: data.files.map(convertFileForUnifiedJSON),
    editorData: convertToEditorData(data),
    recipients: data.recipients.map(
      (recipient, index) => convertRecipient(
        recipient,
        index,
        data.settings.recipientOrder ?? false,
      ),
    ),
    ccs: data.ccs.map(convertCCForUnifiedJSON),
    templates: data.templates.map(convertTemplatesForUnifiedJSON),
    title: data.document.title,
    message: data.document.message,
    coverPage: data.coverPage ? convertCoverPageForUnifiedJSON(data.coverPage) : undefined,
    bulkSend: convertBulkSendForUnifiedJSON(data.bulkSend),
  };
  if (unifiedData.editorData.signers.length === 0) {
    unifiedData.editorData.signers = unifiedData.recipients;
  }
  return unifiedData;
}

function convertPage(page: SaveDataType['editorFields']['pages'][0], files: SaveDataType['files']): Page {
  let snapshotGuid = page.src.match(/\/attachment\/view\?snapshot_guid=(?<snapshot>[\w\d]*)&page=[\d]*/)?.groups?.snapshot ?? '';
  const snapshotGuidFiles = files.filter((file) => file.documentGuid === page.documentId);
  if (snapshotGuidFiles.length > 0 && snapshotGuidFiles[0].guid) {
    // Snapshot Guid from the file is more accurate in some cases
    snapshotGuid = snapshotGuidFiles[0].guid;
  }

  return {
    src: page.src,
    width: page.width,
    height: page.height,
    documentId: page.documentId,
    orientation: page.orientation,
    snapshotGuid,
    cdnSrc: page.cdnSrc,
  };
}

function convertToDocument(file: SaveDataType['files'][0]): Document {
  return {
    id: file.documentGuid ?? '',
    name: file.name,
    editable: file.templateGuid?.length === 0,
    snapshotGuid: file.guid ?? file.rootSnapshotGuid,
  };
}

function convertToEditorData(data: SaveDataType): EditorData {
  const editorData = {
    // Pages are only populated for template requests
    pages: data.editorFields.pages.map((page) => convertPage(page, data.files)),
    fields: data.editorFields.fields.map(convertFields),
    mergeFields: data.editorFields.fields.filter((field) => field.signer === 'sender').map(convertFields),
    pdfFields: [],
    rules: data.editorFields.rules.map(convertRulesForUnifiedJson),
    transform: {
      rotate: 0,
    },
    documents: data.files.map(convertToDocument),
    signers: data.recipients.map((recipient, index) => convertRecipient(
      recipient,
      index,
      data.settings.recipientOrder ?? false,
    )),
    signerOptions: {
      fieldOptions: {
        dateFormat: JSDateFormat.JSDefault,
      },
    },
    editorOptions: {
      allowEditSigners: true,
      allowEditDocuments: true,
      canUseCustomRegexDataValidation: false,
      canUseDataValidation: false,
    },
  };
  return editorData;
}

function mapOperatorForUnifiedJson(operator: string):
StringOperator | NonNumericOperator | NumericOperator | StringArrayOperator {
  switch (operator) {
    case 'is':
      return NonNumericOperator.Is;
    case 'not':
      return NonNumericOperator.Not;
    case 'gt':
      return NumericOperator.Gt;
    case 'lt':
      return NumericOperator.Lt;
    case 'has':
      return StringOperator.Has;
    case 'hasNot':
      return StringOperator.HasNot;
    case 'match':
      return StringOperator.Match;
    case 'any':
      return StringArrayOperator.Any;
    case 'none':
      return StringArrayOperator.None;
    default:
      throw new UnreachableError(operator as never);
  }
}

function convertTriggerForUnifiedJson(
  trigger: Trigger,
): StringArrayTrigger | StringTrigger | NonNumericTrigger | NumericTrigger {
  switch (trigger.operator) {
    case 'is':
    case 'not':
      return {
        id: trigger.id,
        operator: mapOperatorForUnifiedJson(trigger.operator) as NonNumericOperator,
        value: trigger.value as string | boolean,
      };
    case 'gt':
    case 'lt':
      return {
        id: trigger.id,
        operator: mapOperatorForUnifiedJson(trigger.operator) as NumericOperator,
        value: trigger.value as number,
      };
    case 'has':
    case 'hasNot':
    case 'match':
      return {
        id: trigger.id,
        operator: mapOperatorForUnifiedJson(trigger.operator) as StringOperator,
        value: trigger.value as string,
      };
    case 'any':
    case 'none':
      return {
        id: trigger.id,
        operator: mapOperatorForUnifiedJson(trigger.operator) as StringArrayOperator,
        value: trigger.value as string[],
      };
    default:
      throw new UnreachableError(trigger);
  }
}

function convertRulesForUnifiedJson(rule: SaveDataType['editorFields']['rules'][0]): Rule {
  return {
    ...rule,
    triggerOperator: rule.triggerOperator as Rule['triggerOperator'],
    triggers: rule.triggers.map(convertTriggerForUnifiedJson),
  };
}

function convertBulkSendForUnifiedJSON(bulkSend: SaveDataType['bulkSend']): BulkSend {
  // TODO: Create a SignerFile type when the bulksend flow (DEV-12882) is being worked on
  return {
    ...bulkSend,
    signerFile: bulkSend.signerFile ? {
      ...bulkSend.signerFile,
      fields: [],
      guid: uuid(),
      rootSnapshotGuid: uuid(),
      pwRequired: false,
    } : null,
  };
}

function convertCoverPageForUnifiedJSON(coverPage: SaveDataType['coverPage']): CoverPage {
  return {
    ...coverPage,
    to: coverPage.to ?? '',
    from: coverPage.from ?? '',
    message: coverPage.message ?? '',
  };
}

function convertTemplatesForUnifiedJSON(template: SaveDataType['templates'][0]): UnifiedJsonTemplate {
  const { editorFields, document, templateGuid, ...templateCopy } = template;
  return {
    ...templateCopy,
    guid: templateGuid,
    requestType: RequestTypes.SendTemplate,
    recipientReassignment: false,
    recipientOrder: false,
    recipients: template.recipients.map((recipient, index) => convertRecipient(
      recipient,
      index,
      template.recipientOrder,
    )),
    message: template.document.message,
    isOwner: true,
    createdBy: {
      name: '',
      email: '',
      date: '',
    },
    dateFormat: template.dateFormat as PHPDateFormat,
    title: template.document.title,
    jsDateFormat: template.jsDateFormat as JSDateFormat,
    files: template.files.map(convertFileForUnifiedJSON),
    ccs: template.ccs ? template.ccs.map(convertCCForUnifiedJSON) : undefined,
  };
}

function convertCCForUnifiedJSON(cc: CCEmail|CCRole, index: number): PrepAndSendRequest['requestData']['ccs'][0] {
  switch (cc.type) {
    case CCTypes.CCEmail:
      return {
        ...cc,
        id: `${index + 1}`,
        role: cc.role ? convertCCForUnifiedJSON(cc.role, index) as CCRole : undefined,
      };
    case CCTypes.CCRole:
      return {
        ...cc,
      };
    default:
      throw new UnreachableError(cc);
  }
}

function convertRecipient(recipient: SaveDataType['recipients'][0], index: number, recipientOrder: boolean): Recipient {

  switch (recipient.type) {
    case 'fax':
      return {
        ...recipient,
        id: `${index + 1}`,
      };
    case 'signer':
      // eslint-disable-next-line no-case-declarations
      const { smsDeliveryMobileNumber, ...signer } = recipient;
      return identity<Signer>({
        ...signer,
        email: signer.email === '' ? null : signer.email,
        id: `${index + 1}`,
        role: recipient.role
          ? convertRecipient(recipient.role, index, recipientOrder) as Role
          : null,
        attachments: recipient.attachments.map(convertAttachment),
        deliveryType: recipient.deliveryType
          ? recipient.deliveryType as DeliveryType
          : DeliveryType.Email,
        smsDeliveryNumber: recipient.smsDeliveryMobileNumber,
        order: recipientOrder ? index : undefined,
      });
    case 'role':
      return {
        ...recipient,
        id: `${index + 1}`,
        attachments: recipient.attachments.map(convertAttachment),
        order: recipientOrder ? index : undefined,
      };
    default:
      throw new UnreachableError(recipient as never);
  }
}

function convertAttachment(attachment: Attachment): UnifiedJsonAttachment {
  return {
    id: attachment.id,
    name: attachment.name,
    instructions: attachment.instructions ?? '',
    required: attachment.required,
  };
}

function convertFileForUnifiedJSON(file: SaveDataType['files'][0], index: number): File {
  return {
    ...file,
    type: file.type as Type,
    guid: file.guid ?? uuid(),
    rootSnapshotGuid: file.rootSnapshotGuid,
    draftSnapshotGuid: file.draftSnapshotGuid,
    fields: [],
    pwRequired: file.pwRequired ?? false,
    order: index,
  };
}

export function convertForPrepAndSend(data: PrepAndSendRequest): DataSchema {
  const { guid, embeddedData, settings, requestData, user, apiApp, flags } = data;

  return {
    data: {
      guid,
      bulkSend: convertBulkSend(requestData.bulkSend),
      ccs: requestData.ccs.map(convertCCs),
      coverPage: {
        from: requestData.coverPage?.from,
        message: requestData.coverPage?.message ?? requestData.message,
        to: requestData.coverPage?.to,
      },
      document: {
        message: requestData.message,
        title: requestData.title,
      },
      editorFields: {
        pages: requestData.editorData.pages.map(convertPages),
        rules: requestData.editorData.rules,
        fields: requestData.editorData.fields.map(convertField),
      },
      settings: convertSettings(settings),
      files: requestData.files.map(convertFiles),
      recipients: requestData.recipients.map(convertRecipients),
      templates: requestData.templates.map((template) => convertTemplates(template, guid)),
      shouldConvertDocToTemplate: flags.shouldConvertDocToTemplate,
    },
    flags: convertFlags(data),
    user: {
      name: user.name ?? '',
      accountDateFormat: user.accountDateFormat,
      email: user.email,
      isPaidAccount: user.isPaidAccount,
    },
    apiApp,
    embeddedData: embeddedData != null ? {
      docGuids: embeddedData.docGuids,
      editedTemplateGuid: embeddedData.editedTemplateGuid || null,
      isSelfSign: embeddedData.isSelfSign,
      locale: embeddedData.locale,
      parentUrl: embeddedData.parentUrl,
      redirectUrl: embeddedData.redirectUrl,
      usingTemplate: embeddedData.usingTemplate,
    } : undefined,
  };
}

const convertSettings = (
  settings: Settings,
): DataSchema['data']['settings'] => {
  const { requireSignerInfo, ...updatedSettings } = settings;
  return updatedSettings;
};


export const convertTemplates = (
  template: PrepAndSendRequest['requestData']['templates'][0],
  requestGuid: string,
): DataSchema['data']['templates'][0] => {
  const { createdBy, editorData, isOwner, title, message, ...templateCopy } = template;

  return {
    ...templateCopy,
    guid: requestGuid,
    templateGuid: template.guid,
    jsDateFormat: template.jsDateFormat ?? JSDateFormat.JSDefault,
    editorFields: {
      fields: template.editorData?.fields.map(convertField) ?? [],
      pages: template.editorData?.pages.map(convertPages) ?? [],
      rules: template.editorData?.rules ?? [],
    },
    files: template.files?.map(convertFiles) ?? [],
    document: {
      title: template.title,
      message: template.message,
    },
    recipients: template.recipients.map(convertRecipients),
    ccs: template.ccs?.map(convertCCs) ?? [],
  };
};

const convertRecipients = (
  recipient: PrepAndSendRequest['requestData']['recipients'][0],
): DataSchema['data']['recipients'][0] => {

  switch (recipient.type) {
    case 'fax':
      return {
        ...recipient,
        type: RecipientTypes.Fax,

      };
    case 'signer': {
      const role = recipient.role ? convertRecipients(recipient.role) : undefined;
      const { smsDeliveryNumber, ...updatedRecipient } = recipient;

      return {
        ...updatedRecipient,
        email: recipient.email ?? '',
        type: RecipientTypes.Signer,
        deliveryType: recipient.deliveryType ?? 'EMAIL',
        smsDeliveryMobileNumber: recipient.smsDeliveryNumber ?? '',
        role: role?.type === RecipientTypes.Role ? role : undefined,
        accessCode: recipient.accessCode ?? '',
        smsAuthNumber: recipient.smsAuthNumber ?? '',
      };
    }
    case 'role': {
      return {
        ...recipient,
        type: RecipientTypes.Role,
        accessCode: recipient.accessCode ?? '',
        smsAuthNumber: recipient.smsAuthNumber ?? '',
      };
    }

    default: throw new UnreachableError(recipient);
  }
};

const convertFiles = (
  file: PrepAndSendRequest['requestData']['files'][0],
): DataSchema['data']['files'][0] => {
  // These fields aren't used in P&S
  const { tsmGroupGuid, status, order, pageCount, ...fileCopy } = file;
  return {
    ...fileCopy,
    fields: file.fields.filter((field) => field.signer === 'sender').map(convertField),
  };
};

const convertFlags = (
  data: PrepAndSendRequest,
): DataSchema['flags'] => {
  const { options, accountSettings, flags } = data;

  return {
    isMobileAppRequest: Boolean(flags.isMobileAppRequest),
    requestType: flags.requestType,

    hideProgressStepper: options.hideProgressStepper === true,
    hideStepperContainer: options.hideStepperContainer === true,

    chargeType: accountSettings.chargeType,
    totalPriceInCents: accountSettings.totalPriceInCents,
    freeFaxPagesLeft: accountSettings.freeFaxPagesLeft,
    faxPageCurrentCount: accountSettings.faxPageCurrentCount,
    overageFaxMaxLimitInCents: accountSettings.overageFaxMaxLimitInCents,
    pricePerFaxInCents: accountSettings.pricePerFaxInCents,
    initialPageLimit: accountSettings.initialPageLimit,
    overagePriceInCentsPerPage: accountSettings.overagePriceInCentsPerPage,
    internationalMultiplier: accountSettings.internationalMultiplier,
    allowTemplateDelete: accountSettings.allowTemplateDelete,

    needsCharge: accountSettings.needsCharge,
    allowCreateEditTemplate: accountSettings.allowCreateEditTemplate === true,
    allowTemplatePreview: accountSettings.allowTemplatePreview === true,
    allowOptionalTemplateSigners: accountSettings.allowOptionalTemplateSigners === true,
    uploadIntegrations: convertIntegrations(accountSettings.uploadIntegrations),
    allowSmsSignatureDelivery: accountSettings.allowSmsSignatureDelivery,
    allowRecipientAccessCodes: accountSettings.allowRecipientAccessCodes,
    allowBulkSend: accountSettings.allowBulkSend,
    allowQualifiedSignature: accountSettings.allowQualifiedSignature,
    allowRecipientAttachments: accountSettings.allowRecipientAttachments,
    allowIntegrationsUpload: accountSettings.allowIntegrationsUpload,
    allowRecipientReassignment: accountSettings.allowRecipientReassignment,
    allowRecipientSmsAuthNumbers: accountSettings.allowRecipientSmsAuthNumbers,
    allowSmsAuth: accountSettings.allowSmsAuth,
    alwaysSendMeACopy: accountSettings.alwaysSendMeACopy,
    isAtDocumentLimit: accountSettings.isAtDocumentLimit,
    isAtTemplateLimit: accountSettings.isAtTemplateLimit,
    alwaysSendOthersACopy: accountSettings.alwaysSendOthersACopy,
    canAccessRecipientAccessCodes: accountSettings.canAccessRecipientAccessCodes,
    shouldRouteToPrepAndSend: flags.shouldRouteToPrepAndSend,
    blockCC: options.blockCC,
    displayDocToTemplateBanner: flags.displayDocToTemplateBanner,
    canUpgradeTierSmsSignatureDelivery: accountSettings.canUpgradeTierSmsSignatureDelivery,
    shouldConvertDocToTemplate: flags.shouldConvertDocToTemplate,
    templateConversionCandidateId: flags.templateConversionCandidateId,
    lockDocumentMessage: options.lockDocumentMessage,
    lockDocumentTitle: options.lockDocumentTitle,
    forceSignerRoles: options.forceSignerRoles,
    forceSubjectMessage: options.forceSubjectMessage,
    isHelloFax: flags.isHelloFax ? flags.isHelloFax : undefined,
    noMultiFaxes: accountSettings.noMultiFaxes,
    hasHipaa: accountSettings.hasHIPAA,
    hasTemplateReuseWarning: flags.hasTemplateReuseWarning,
    isNewHsPlan: accountSettings.isNewHsPlan,
    isTransactionalPlan: accountSettings.isTransactionalPlan,
    isResend: flags.isResend,
    requireSignerInfo: options.requireSignerInfo,
    maxTemplateNumber: accountSettings.maxTemplateNumber,
    isSmsToolsOn: accountSettings.isSMSToolsOn,
  };
};

const convertPages = (
  page: PrepAndSendRequest['requestData']['editorData']['pages'][0],
): DataSchema['data']['editorFields']['pages'][0] => {
  const { snapshotGuid, ...pageCopy } = page;
  return {
    ...pageCopy,
    orientation: page.orientation === 0 ? 0 : 1,
  };
};

const convertCCs = (
  cc: PrepAndSendRequest['requestData']['ccs'][0],
): DataSchema['data']['ccs'][0] => {
  if (cc.type === 'ccRole') {
    return {
      type: CCTypes.CCRole,
      id: cc.id,
      name: cc.name,
    };
  } else if (cc.type === 'ccEmail') {
    const role = cc.role ? convertCCs(cc.role) : null;
    return {
      type: CCTypes.CCEmail,
      email: cc.email,
      id: cc.id,
      role: (role && role.type === CCTypes.CCRole) ? role : null,
    };
  }

  throw new UnreachableError(cc);
};

const convertField = (
  field: PrepAndSendRequest['requestData']['editorData']['fields'][0],
): DataSchema['data']['editorFields']['fields'][0] => {

  const formattedField = {
    type: field.type,
    pageIndex: field.pageIndex,
    documentId: field.documentId,
    id: field.id,
    name: field.name ?? '',
    x: field.x,
    y: field.y,
    width: field.width,
    height: field.height,
    signer: field.signer,
    required: field.required ?? false,
    readOnly: field.readOnly ?? false,
    // linkId: field.linkId ?? undefined,
    isDisabled: field.isDisabled ? true : undefined,
  };

  switch (field.type) {
    case 'text':
      return ({
        ...formattedField,
        type: FieldTypes.Text,
        fontSize: <FontSize>(field.fontSize) ?? FontSize.Twelve,
        fontFamily: <FontFamily>(field.fontFamily) ?? FontFamily.Arial,
        value: field.value ?? undefined,
        autoFillType: getAutoFilledType(field.autoFillType),
      });

    case 'signature':
      return ({
        ...formattedField,
        type: FieldTypes.Signature,
        // color: <Color>(field.color) ?? undefined,
        signature: field.signature ? <Signature>({
          type: SignatureType.Signature,
          guid: field.signature.guid,
        }) : undefined,
      });

    case 'initials':
      return ({
        ...formattedField,
        type: FieldTypes.Initials,
        // color: <Color>(field.color) ?? undefined,
        signature: field.signature ? <Signature>({
          type: SignatureType.Initials,
          guid: field.signature.guid,
        }) : undefined,
      });

    case 'date':
      return ({
        ...formattedField,
        type: FieldTypes.Date,
        fontSize: <FontSize>(field.fontSize) ?? FontSize.Twelve,
        fontFamily: <FontFamily>(field.fontFamily) ?? FontFamily.Arial,
        value: field.value ?? null,
        dateFormat: <JSDateFormat>(field.dateFormat) ?? JSDateFormat.JSDefault,
      });

    case 'checkbox':
      return ({
        ...formattedField,
        type: FieldTypes.Checkbox,
        checked: field.checked ?? undefined,
        requirement: field.requirement ?? undefined,
        groupLabel: field.groupLabel ?? undefined,
        group: field.group ?? undefined,
      });

    case 'radiobutton':
      return ({
        ...formattedField,
        type: FieldTypes.Radio,
        checked: field.checked ?? false,
        requirement: field.requirement ?? '',
        groupLabel: field.groupLabel ?? undefined,
        group: field.group ?? undefined,
      });

    case 'dropdown':
      return ({
        ...formattedField,
        type: FieldTypes.Dropdown,
        fontSize: <FontSize>(field.fontSize) ?? FontSize.Twelve,
        fontFamily: <FontFamily>(field.fontFamily) ?? FontFamily.Arial,
        options: field.options ?? [],
      });

    case 'hyperlink':
      return ({
        ...formattedField,
        type: FieldTypes.Hyperlink,
        fontSize: <FontSize>(field.fontSize) ?? FontSize.Twelve,
        fontFamily: <FontFamily>(field.fontFamily) ?? FontFamily.Arial,
        value: field.value ?? null,
        url: field.url ?? '',
      });
    default:
      throw new UnreachableError(field);
  }
};

function convertBulkSend(
  bulkSend: PrepAndSendRequest['requestData']['bulkSend'],
): DataSchema['data']['bulkSend'] {
  const { exampleFields, signerFile } = bulkSend;

  if (signerFile) {
    return {
      exampleFields,
      signerFile: {
        name: signerFile.name,
        status: signerFile.status ?? FilePollResponseStatus.Ok,
        type: UserFileTypes.Signer,
      },
    };

  }
  return { exampleFields, signerFile };
}
